import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import {
  Badge, Banner, EmptyState, PageLoading, Select,
} from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { ScheduleReport, TeamMember } from '../../types';

/**
 * Schedule report — one day's adherence.
 *
 * For the chosen date: who was on each rep's journey plan, how many were
 * visited, and how many were missed — with the missed shops named. The
 * "planned" beat is the same source the coverage report counts against,
 * so the two never disagree. Managers see their own team; admins everyone.
 */

function today() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const WEEKDAY = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function pct(n: number, d: number) {
  return d > 0 ? Math.round((n / d) * 100) : 0;
}

export default function ScheduleReportPage() {
  const toast = useToast();

  const [date, setDate] = useState(today);
  const [userId, setUserId] = useState('');
  const [reps, setReps] = useState<{ id: string; name: string }[]>([]);

  const [data, setData] = useState<ScheduleReport | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<ScheduleReport>('/visits/schedule/report', {
        date,
        userId: userId || undefined,
      });
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load the schedule report.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, userId]);

  useEffect(() => { void run(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    api.get<TeamMember[]>('/users', { role: 'sales_rep', status: 'active' })
      .then((us) => setReps(us.map((u) => ({ id: u.id, name: u.fullName }))))
      .catch(() => { /* rep filter optional */ });
  }, []);

  const t = data?.totals;

  return (
    <>
      <PageHeader
        title="Schedule report"
        subtitle="For one day: who was on the beat, who got visited, and who was missed. Managers see their team; admins everyone."
        actions={(
          <button type="button" className="btn btn-primary" onClick={() => void run()}>
            <Icon name="history" size={15} /> Refresh
          </button>
        )}
      />

      <div className="page col gap-16">
        <div className="card" style={{ padding: 14 }}>
          <div className="row gap-12" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ margin: 0 }}>
              <label>Date</label>
              <input type="date" className="input" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div style={{ minWidth: 190 }}>
              <Select label="Rep" value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">All reps</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </Select>
            </div>
            <button type="button" className="btn btn-primary" onClick={() => void run()}>Apply</button>
          </div>
        </div>

        {loading ? <PageLoading rows={5} /> : !data ? (
          <Banner kind="info">No data for this day yet.</Banner>
        ) : (
          <>
            <div className="row gap-8" style={{ color: 'var(--ink-muted)', fontSize: 13.5 }}>
              <Icon name="date" size={15} />
              <span>{WEEKDAY[data.weekday]} · {data.date}</span>
            </div>

            {!data.hasPlan && data.note && <Banner kind="info">{data.note}</Banner>}

            {/* Summary tiles */}
            <div className="row gap-12" style={{ flexWrap: 'wrap' }}>
              <StatCard label="Scheduled" value={t!.scheduled} tone="ink" />
              <StatCard label="Visited" value={t!.visited} tone="success"
                sub={t!.scheduled ? `${pct(t!.visited, t!.scheduled)}% of plan` : undefined} />
              <StatCard label="Missed" value={t!.missed} tone="danger"
                sub={t!.scheduled ? `${pct(t!.missed, t!.scheduled)}% of plan` : undefined} />
              <StatCard label="Off-plan calls" value={t!.offPlan} tone="warning"
                sub="Visited, not scheduled" />
            </div>

            {/* Per-rep breakdown */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 14px', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>
                By rep
              </div>
              {data.perRep.length === 0 ? (
                <div style={{ padding: 20 }}>
                  <EmptyState title="Nothing scheduled or visited" body="No plan and no calls for this day." />
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Rep</th>
                        <th style={{ textAlign: 'right' }}>Scheduled</th>
                        <th style={{ textAlign: 'right' }}>Visited</th>
                        <th style={{ textAlign: 'right' }}>Missed</th>
                        <th style={{ textAlign: 'right' }}>Off-plan</th>
                        <th style={{ textAlign: 'right' }}>Coverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.perRep.map((r) => (
                        <tr key={r.userId}>
                          <td>{r.name}</td>
                          <td style={{ textAlign: 'right' }}>{r.scheduled}</td>
                          <td style={{ textAlign: 'right', color: 'var(--success)' }}>{r.visited}</td>
                          <td style={{ textAlign: 'right', color: r.missed ? 'var(--danger)' : undefined }}>{r.missed}</td>
                          <td style={{ textAlign: 'right' }}>{r.offPlan}</td>
                          <td style={{ textAlign: 'right' }}>
                            {r.scheduled ? <Badge tone={pct(r.visited, r.scheduled) >= 80 ? 'success' : pct(r.visited, r.scheduled) >= 50 ? 'warning' : 'danger'}>{pct(r.visited, r.scheduled)}%</Badge> : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Missed shops */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 14px', fontWeight: 700, borderBottom: '1px solid var(--border)' }}>
                Missed shops ({data.missed.length})
              </div>
              {data.missed.length === 0 ? (
                <div style={{ padding: 20 }}>
                  <EmptyState title="Nothing missed" body="Every scheduled shop was visited." />
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr><th>Shop</th><th>Area</th><th>Rep</th></tr>
                    </thead>
                    <tbody>
                      {data.missed.map((m) => (
                        <tr key={`${m.userId}-${m.customerId}`}>
                          <td>{m.customerName}</td>
                          <td>{m.area ?? '—'}</td>
                          <td style={{ color: 'var(--ink-muted)' }}>{m.repName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function StatCard({ label, value, tone, sub }: {
  label: string; value: number; tone: 'ink' | 'success' | 'danger' | 'warning'; sub?: string;
}) {
  const color = tone === 'ink' ? 'var(--ink)'
    : tone === 'success' ? 'var(--success)'
      : tone === 'danger' ? 'var(--danger)'
        : 'var(--warning)';
  return (
    <div className="card" style={{ padding: 14, minWidth: 150, flex: '1 1 150px' }}>
      <div style={{ fontSize: 11.5, letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 700, color: 'var(--ink-faint)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--ink-faint)' }}>{sub}</div>}
    </div>
  );
}
